import React, { useCallback, useEffect, useState } from "react";
import { getLeadInsights } from "../../api/services/analyticsService";

const BAND_COLOR = { Hot: "#b02a37", Warm: "#c98a00", Cold: "#6c757d" };

function BarList({ title, rows, hint }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <section className="card p-3 h-100" aria-label={title}>
      <h3 className="h6 mb-1">{title}</h3>
      {hint && <div className="small text-muted mb-3">{hint}</div>}
      <ul className="list-unstyled mb-0 li-bars">
        {rows.map((r) => (
          <li key={r.name} className="mb-2">
            <div className="d-flex justify-content-between small"><span className="text-break">{r.name}</span><span className="text-muted">{r.count} · {r.percent}% · avg score {r.averageScore}</span></div>
            <div className="li-track" role="img" aria-label={`${r.name}: ${r.count} leads, ${r.percent} percent`}><div className="li-fill" style={{ width: `${Math.max(3, (r.count / max) * 100)}%` }} /></div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Lead source / intent / score view: where leads come from, what they want, and how warm they are. */
export default function LeadInsights() {
  const [state, setState] = useState("loading"); // loading | ready | empty | error
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setState("loading");
    getLeadInsights()
      .then((res) => { if (!res) { setData(null); setState("empty"); } else { setData(res); setState("ready"); } })
      .catch((err) => { setError(err.message || "Unable to load lead insights."); setState("error"); });
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="mt-4" aria-labelledby="li-title">
      <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mb-2">
        <div><h2 className="h5 mb-0" id="li-title">Lead insights</h2><div className="small text-muted">Source, intent and score of all leads in this workspace.</div></div>
        {state === "ready" && <div className="small text-muted">{data.total} leads · average score {data.averageScore}</div>}
      </div>
      {state === "loading" && <p className="text-muted" role="status">Loading lead insights…</p>}
      {state === "error" && <div className="alert alert-danger py-2" role="alert">{error} <button type="button" className="btn btn-link btn-sm p-0" onClick={load}>Retry</button></div>}
      {state === "empty" && <div className="card p-4 text-center text-muted">No leads yet. Leads captured by your chat widget or added in Lead Management will appear here.</div>}
      {state === "ready" && (
        <div className="row g-3">
          <div className="col-lg-4"><BarList title="By source" rows={data.bySource} /></div>
          <div className="col-lg-4"><BarList title="By intent" rows={data.byIntent} /></div>
          <div className="col-lg-4">
            <section className="card p-3 h-100" aria-label="Score bands">
              <h3 className="h6 mb-1">Score bands</h3>
              <div className="small text-muted mb-3">Hot 75-100 · Warm 40-74 · Cold 0-39</div>
              <div className="li-stack" role="img" aria-label={data.scoreBands.map((b) => `${b.name} ${b.count}`).join(", ")}>
                {data.scoreBands.filter((b) => b.count > 0).map((b) => <div key={b.name} style={{ width: `${b.percent}%`, background: BAND_COLOR[b.name] }} title={`${b.name}: ${b.count}`} />)}
              </div>
              <ul className="list-unstyled mt-3 mb-3">
                {data.scoreBands.map((b) => (
                  <li key={b.name} className="d-flex justify-content-between small py-1"><span><span className="li-dot" style={{ background: BAND_COLOR[b.name] }} />{b.name} ({b.range})</span><strong>{b.count} · {b.percent}%</strong></li>
                ))}
              </ul>
              <div className="small text-muted mb-1">By status</div>
              <div className="d-flex flex-wrap gap-2">{data.byStatus.map((s) => <span key={s.name} className="badge text-bg-light border">{s.name} {s.count}</span>)}</div>
            </section>
          </div>
        </div>
      )}
    </section>
  );
}
